import React, { useCallback, useMemo } from 'react';
import { Store, FranchiseGroup } from '../../../types';
import FranchiseSelector, { FranchiseOption } from './FranchiseSelector';

/**
 * Utility functions to convert between FranchiseOption and FranchiseGroup.
 */
const franchiseGroupToOption = (group: FranchiseGroup): FranchiseOption => ({
  code: group.id,
  name: group.name,
  storeCount: group.storeCount,
});

const franchiseOptionToGroup = (option: FranchiseOption): FranchiseGroup => ({
  id: option.code,
  name: option.name,
  storeCount: option.storeCount,
});

// Special handling for the "All" option
const isAllOption = (option: FranchiseOption): boolean => option.code === 'all';

/**
 * Base props shared by both single and multiple selectors.
 */
interface BaseFranchiseSelectorAdapterProps {
  label: string;
  placeholder: string;
  disabled?: boolean;
  testId?: string;
}

/**
 * Props for single selection mode.
 */
interface SingleFranchiseSelectorAdapterProps extends BaseFranchiseSelectorAdapterProps {
  multiple?: false;
  value?: FranchiseGroup | null;
  onChange: (value: FranchiseGroup | null, stores: Store[]) => void;
}

/**
 * Props for multiple selection mode.
 */
interface MultipleFranchiseSelectorAdapterProps extends BaseFranchiseSelectorAdapterProps {
  multiple: true;
  value?: FranchiseGroup[];
  onChange: (value: FranchiseGroup[], stores: Store[]) => void;
}

/** Union type for props */
type FranchiseSelectorAdapterProps =
  | SingleFranchiseSelectorAdapterProps
  | MultipleFranchiseSelectorAdapterProps;

/** Type guard for multiple selection props */
function isMultipleProps(
  props: FranchiseSelectorAdapterProps
): props is MultipleFranchiseSelectorAdapterProps {
  return props.multiple === true;
}

const FranchiseSelectorAdapter: React.FC<FranchiseSelectorAdapterProps> = (props) => {
  const { label, testId, placeholder, disabled = false } = props;
  const multiple = isMultipleProps(props);

  // Memoize the converted value to avoid re-computation on each render.
  const convertedValue = useMemo(() => {
    return multiple
      ? props.value?.map(franchiseGroupToOption) || []
      : props.value
      ? franchiseGroupToOption(props.value)
      : null;
  }, [props.value, multiple]);

  // Memoized handler to convert FranchiseOption changes back to FranchiseGroup.
  const handleChange = useCallback(
    (value: FranchiseOption | FranchiseOption[] | null, stores: Store[]) => {
      if (multiple) {
        // For multiple selection, convert all options to groups
        const groups = (value as FranchiseOption[]).map(franchiseOptionToGroup);
        props.onChange(groups, stores);
      } else {
        // For single selection
        if (value && isAllOption(value as FranchiseOption)) {
          // Handle "All" option specially
          const group = franchiseOptionToGroup(value as FranchiseOption);
          (props as SingleFranchiseSelectorAdapterProps).onChange(group, stores);
        } else {
          // Handle regular option
          const group = value ? franchiseOptionToGroup(value as FranchiseOption) : null;
          (props as SingleFranchiseSelectorAdapterProps).onChange(group, stores);
        }
      }
    },
    [props, multiple]
  );

  return multiple ? (
    <FranchiseSelector
      label={label}
      data-testid={ testId ?? 'multipleFranchiseSelector' }
      placeholder={placeholder}
      multiple={true}
      value={convertedValue as FranchiseOption[]}
      onChange={handleChange as (value: FranchiseOption[], stores: Store[]) => void}
      disabled={disabled}
    />
  ) : (
    <FranchiseSelector
      label={label}
      data-testid={ testId ?? 'singleFranchiseSelector' }
      placeholder={placeholder}
      multiple={false}
      value={convertedValue as FranchiseOption | null}
      onChange={handleChange as (value: FranchiseOption | null, stores: Store[]) => void}
      disabled={disabled}
    />
  );
};

export default FranchiseSelectorAdapter;
